// Design tokens matching the Inventra brand mark (client/src/images/inventra_logo.png) — navy
// blue + steel grey, pixel-sampled directly from the logo file (dominant clusters averaged to
// #081d53 navy and #787878 grey) rather than eyeballed. `colors` (light) and `darkColors` (dark)
// share the same hue family at different lightness — the same M3-style "fixed"/"container"
// relationship used throughout this file (e.g. dark-mode's inversePrimary is light-mode's
// primary, and vice versa) — so the app's light/dark toggle stays hue-consistent with the brand
// in both modes instead of just in one.
//
// success/warning/error are deliberately NOT tied to the brand hue (unlike the previous emerald
// theme, where success == primary) — the brand is navy+grey, so status colors stay their
// conventional universal hues (green/amber/red) rather than overloading one of them as "the
// brand color."

export const colors = {
  primary: '#0a1e52',
  primaryDark: '#050f2e',
  primaryLight: '#7089db',
  onPrimary: '#ffffff',

  secondary: '#6b6f76',
  onSecondary: '#ffffff',
  secondaryContainer: '#e4e6e9',
  onSecondaryContainer: '#3a3d42',

  tertiary: '#3e5c8a',
  onTertiary: '#ffffff',
  tertiaryContainer: '#d7e0f0',
  onTertiaryContainer: '#1b2c47',

  error: '#ba1a1a',
  errorLight: '#ffdad6',
  errorDark: '#93000a',
  onError: '#ffffff',

  success: '#1b7a43',
  successLight: '#bff0ce',
  warning: '#8a5300',
  warningLight: '#ffdead',

  background: '#f5f6f8',
  onBackground: '#1a1e27',

  surface: '#f5f6f8',
  surfaceBright: '#ffffff',
  surfaceDim: '#e4e7ec',
  surfaceContainerLowest: '#ffffff',
  surfaceContainerLow: '#eef0f3',
  surfaceContainer: '#e8ebef',
  surfaceContainerHigh: '#e2e5ea',
  surfaceContainerHighest: '#dce0e6',
  surfaceVariant: '#dee2e8',

  onSurface: '#1a1e27',
  onSurfaceVariant: '#4a4f5a',
  outline: '#7a7f8a',
  outlineVariant: '#c7cbd3',

  inverseSurface: '#2b2f3a',
  inverseOnSurface: '#eef0f3',
  inversePrimary: '#7089db',
};

export const darkColors = {
  primary: '#7089db',
  primaryDark: '#4a63b0',
  primaryLight: '#a8bcf0',
  onPrimary: '#0a1e52',

  secondary: '#a8adb5',
  onSecondary: '#24262a',
  secondaryContainer: '#4a4e56',
  onSecondaryContainer: '#e4e6e9',

  tertiary: '#9bb0d9',
  onTertiary: '#1b2c47',
  tertiaryContainer: '#3e5c8a',
  onTertiaryContainer: '#d7e0f0',

  error: '#ffb4ab',
  errorLight: '#ffdad6',
  errorDark: '#93000a',
  onError: '#690005',

  success: '#5cc384',
  successLight: '#9fe8b8',
  warning: '#ffb951',
  warningLight: '#5c3f00',

  background: '#0e1420',
  onBackground: '#e3e6ec',

  surface: '#0e1420',
  surfaceBright: '#38404e',
  surfaceDim: '#0e1420',
  surfaceContainerLowest: '#080c14',
  surfaceContainerLow: '#141b29',
  surfaceContainer: '#182030',
  surfaceContainerHigh: '#212a3b',
  surfaceContainerHighest: '#2b3548',
  surfaceVariant: '#2b3548',

  onSurface: '#e3e6ec',
  onSurfaceVariant: '#b7bcc7',
  outline: '#8a93a3',
  outlineVariant: '#3c4658',

  inverseSurface: '#e3e6ec',
  inverseOnSurface: '#232a38',
  inversePrimary: '#0a1e52',
};

export const typography = {
  fontFamily: '"Inter", Roboto, Helvetica, Arial, sans-serif',
  codeFontFamily: '"JetBrains Mono", ui-monospace, monospace',
};

// Flat 2px corner radius everywhere (buttons, inputs, cards, dialogs, chips, ...) per feedback
// that the rounder DESIGN.md-derived values (12–24px) read as too soft/bubbly for a dense
// enterprise app. One value reused for every slot rather than a scale, by request.
export const radius = {
  sm: 2,
  md: 2,
  lg: 2,
  xl: 2,
  full: 2,
};

export const spacing = {
  base: 4,
  xs: 8,
  sm: 12,
  md: 16,
  lg: 24,
  xl: 32,
};

// Glassmorphism surface recipes from DESIGN.md's "Elevation & Depth" section, one recipe per
// mode. Applied selectively (main containers/cards/dialogs/nav shell) — transient overlays that
// must stay legible over dense content (Menu/Select/Autocomplete popups) intentionally stay
// solid/opaque instead, see theme/index.ts. Borders/shadows are intentionally low-contrast —
// visible enough to read as a surface edge, not a hard outline.
export const glassDark = {
  panelBg: 'rgba(26, 36, 53, 0.6)',
  panelBorder: 'rgba(255, 255, 255, 0.06)',
  panelBlur: 16,
  panelShadow: '0 24px 60px rgba(0, 0, 0, 0.22)',

  cardBg: 'rgba(23, 33, 48, 0.55)',
  cardBorderTop: 'rgba(255, 255, 255, 0.04)',
  cardBlur: 10,

  heavyBg: 'rgba(21, 32, 49, 0.88)',
  heavyBorder: 'rgba(255, 255, 255, 0.08)',
  heavyBlur: 28,

  hairline: 'rgba(255, 255, 255, 0.07)',
};

export const glassLight = {
  panelBg: 'rgba(255, 255, 255, 0.65)',
  panelBorder: 'rgba(15, 23, 42, 0.06)',
  panelBlur: 16,
  panelShadow: '0 24px 60px rgba(15, 23, 42, 0.08)',

  cardBg: 'rgba(255, 255, 255, 0.72)',
  cardBorderTop: 'rgba(15, 23, 42, 0.03)',
  cardBlur: 10,

  heavyBg: 'rgba(255, 255, 255, 0.92)',
  heavyBorder: 'rgba(15, 23, 42, 0.07)',
  heavyBlur: 28,

  hairline: 'rgba(15, 23, 42, 0.07)',
};
